import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { PrimaryGrid } from '@ui/components/common/Grid';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Location } from 'iconsax-react';
import { StyledMenu } from '@ui/components/common/Menu';
import ReassignPickupLocationMenu from '../ReassignPickupLocationMenu';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import ReassignPickupLocationDialog from './ReassignPickupLocationDialog';
import ReassignVehicleDialog from './ReassignVehicleDialog';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getAttendeesByVehicleIdAsync, unassignAttendeeFromVehicleAsync, assignAttendeeToVehicleAsync, assignCoordinatorAsync, reassignAttendeesToPickupLocationAsync } from '@application/features/attendeePickupAssignment/attendeePickupAssignmentThunk';
import { getPickupLocationsNotPaginatedByEventIdAsync, getPickupLocationByIdAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { getFamilyFestPickupLocationByIdAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
import { toast } from 'react-toastify';
import { formatRegisteredDateOnly } from '@application/utilities/date-helper';
import { useParams } from 'react-router-dom';
import { getVehiclesByPickupLocationIdAsync } from '@application/features/pickupLocationVehicle/pickupLocationVehicleThunk';
import { getFamilyFestAttendeesByVehicleIdAsync, unassignFamilyFestAttendeeFromVehicleAsync, assignFamilyFestAttendeeToVehicleAsync, reassignFamilyFestAttendeesToPickupLocationAsync } from '@application/features/familyFest/familyFestPickupAssignmentThunk';
const ViewAttendeesDialog = ({ open, onClose, vehicleNumber, vehicleId, locationId, onConfirm, hideCoordinatorButton = false, hideActionsMenu = false, hideBulkActions = false, isFamilyFest }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const { eventId } = useParams();
    const dispatch = useAppDispatch();
    const { vehicleAttendees: standardVehicleAttendees } = useAppSelector((state) => state.attendeePickupAssignment);
    const { vehicleAttendees: familyFestVehicleAttendees } = useAppSelector((state) => state.familyFestPickupAssignment);
    const { pickupLocationsList } = useAppSelector((state) => state.pickupLocation);
    const vehicleAttendees = isFamilyFest ? familyFestVehicleAttendees : standardVehicleAttendees;
    const [searchValue, setSearchValue] = useState('');
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [pickupLocationSearchValue, setPickupLocationSearchValue] = useState('');
    const [reassignPickupAnchorEl, setReassignPickupAnchorEl] = useState(null);
    const [bulkMenuAnchorEl, setBulkMenuAnchorEl] = useState(null);
    const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
    const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);
    const [confirmCoordinatorOpen, setConfirmCoordinatorOpen] = useState(false);
    const [pendingCoordinatorId, setPendingCoordinatorId] = useState(null);
    const [reassignPickupDialogOpen, setReassignPickupDialogOpen] = useState(false);
    const [reassignVehicleDialogOpen, setReassignVehicleDialogOpen] = useState(false);
    const [confirmUnassignOpen, setConfirmUnassignOpen] = useState(false);
    const [attendeeToUnassign, setAttendeeToUnassign] = useState(null);
    const [confirmBulkUnassignOpen, setConfirmBulkUnassignOpen] = useState(false);
    const [attendeeToReassign, setAttendeeToReassign] = useState(null);
    const [attendeeToReassignPickup, setAttendeeToReassignPickup] = useState(null);
    const [bulkReassignVehicleDialogOpen, setBulkReassignVehicleDialogOpen] = useState(false);
    const [hasCoordinator, setHasCoordinator] = useState(false);
    const [coordinatorName, setCoordinatorName] = useState(null);
    const [confirmBulkReassignOpen, setConfirmBulkReassignOpen] = useState(false);
    const [selectedPickupLocation, setSelectedPickupLocation] = useState(null);
    useEffect(() => {
        if (open && vehicleId && vehicleAttendees?.items) {
            const items = vehicleAttendees.items;
            const coordinator = items.find((a) => a.isCoordinator);
            if (coordinator) {
                setHasCoordinator(true);
                setCoordinatorName(coordinator.name || null);
            }
        }
        else if (!open) {
            setHasCoordinator(false);
            setCoordinatorName(null);
        }
    }, [open, vehicleId, vehicleAttendees]);
    // Grid column fields don't always match the backend's sort keys.
    const VEHICLE_ATTENDEE_SORT_FIELD_MAP = {
        name: 'name',
        company: 'companyname',
        type: 'attendeetype',
        registered: 'registereddate'
    };
    // Fetch attendees by vehicle ID when dialog opens
    useEffect(() => {
        if (open && vehicleId) {
            const sort = sortModel[0];
            if (isFamilyFest) {
                dispatch(getFamilyFestAttendeesByVehicleIdAsync({
                    vehicleId: vehicleId,
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    sortBy: sort ? (VEHICLE_ATTENDEE_SORT_FIELD_MAP[sort.field] ?? sort.field) : undefined,
                    sortOrder: sort?.sort ?? undefined
                }));
            }
            else {
                dispatch(getAttendeesByVehicleIdAsync({
                    vehicleId: vehicleId,
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined
                }));
            }
        }
    }, [open, vehicleId, paginationModel.page, paginationModel.pageSize, sortModel, searchValue, dispatch, isFamilyFest]);
    // Fetch pickup locations when dialog opens or search changes
    useEffect(() => {
        if (open && eventId) {
            dispatch(getPickupLocationsNotPaginatedByEventIdAsync({
                eventId: eventId,
                includeClosed: false,
                searchString: pickupLocationSearchValue || undefined,
                pickupLocationId: locationId
            }));
        }
    }, [open, eventId, pickupLocationSearchValue, dispatch]);
    // Map pickup locations to options
    const pickupLocationOptions = useMemo(() => {
        if (!pickupLocationsList)
            return [];
        return pickupLocationsList.map((location) => ({
            id: location.id,
            name: location.name,
            address: location.address,
            eventName: location.eventName || '',
            capacity: {
                remaining: location.assignedAttendees,
                total: location.totalCapacity
            }
        }));
    }, [pickupLocationsList]);
    // Map attendees to ViewAttendeeRow format
    const attendees = useMemo(() => {
        if (!vehicleAttendees?.items)
            return [];
        return vehicleAttendees.items.map((attendee, index) => ({
            id: attendee.id,
            name: attendee.name || '',
            company: attendee.companyName || '',
            type: attendee.attendeeType || '',
            primaryPerson: attendee.primaryAttendeeName || '-',
            registered: formatRegisteredDateOnly(attendee.createdDate) || '',
            isCoordinator: attendee.isCoordinator || false,
            isBirthday: attendee.hasBirthdayOnEventDate
        }));
    }, [vehicleAttendees]);
    const currentCoordinator = attendees.find((a) => a.isCoordinator);
    const pendingCoordinator = attendees.find((a) => a.id === pendingCoordinatorId);
    const applyCoordinator = async (attendeeId) => {
        if (!vehicleId || isFamilyFest)
            return;
        try {
            const request = {
                attendeeId: attendeeId,
                vehicleId: vehicleId
            };
            const response = await dispatch(assignCoordinatorAsync({ request })).unwrap()
                .then((response) => {
                dispatch(getVehiclesByPickupLocationIdAsync({
                    pickupLocationId: String(locationId),
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                }));
                return response;
            });
            if (response.isSuccess) {
                toast.success(response.message ||
                    t('events.eventDetails.transportTab.coordinatorAssignedSuccessfully'));
                // Refresh the attendee list
                setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                dispatch(getAttendeesByVehicleIdAsync({
                    vehicleId: vehicleId,
                    pageNumber: 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined
                }));
                // Update coordinator state
                setHasCoordinator(true);
                const newCoordinator = attendees.find((a) => a.id === attendeeId);
                setCoordinatorName(newCoordinator?.name || null);
            }
            else {
                toast.error(response.message ||
                    t('events.eventDetails.transportTab.coordinatorAssignFailed'));
            }
        }
        catch (error) {
            const errorMessage = error?.message ||
                t('events.eventDetails.transportTab.coordinatorAssignFailed');
            toast.error(errorMessage);
        }
    };
    const handleAssignCoordinatorClick = async (row) => {
        if (row.isCoordinator)
            return;
        // Check if coordinator exists
        if (!hasCoordinator) {
            await applyCoordinator(row.id);
            return;
        }
        setPendingCoordinatorId(row.id);
        setConfirmCoordinatorOpen(true);
    };
    const filteredAttendees = attendees;
    const columns = [
        {
            field: 'name',
            headerName: t('events.eventDetails.transportTab.name'),
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                const row = params.row;
                return (_jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 40, children: [_jsx(Typography, { variant: 'body2', children: row.name || '—' }), row.isBirthday && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', children: t('events.eventDetails.transportTab.birthday') }))] }));
            }
        },
        {
            field: 'company',
            headerName: t('events.eventDetails.transportTab.company'),
            flex: 1,
            minWidth: 100,
            renderCell: (params) => {
                const row = params.row;
                return row.company || '—';
            }
        },
        {
            field: 'type',
            headerName: t('events.eventDetails.transportTab.type'),
            flex: 0.8,
            minWidth: 100,
            renderCell: (params) => {
                const row = params.row;
                return row.type || '—';
            }
        },
        ...(isFamilyFest
            ? [
                {
                    field: 'primaryPerson',
                    headerName: t('events.eventDetails.transportTab.primaryPerson'),
                    flex: 1,
                    minWidth: 140,
                    sortable: false,
                    filterable: false
                }
            ]
            : []),
        {
            field: 'registered',
            headerName: t('events.eventDetails.transportTab.registered'),
            flex: 1.5,
            minWidth: 200,
            renderCell: (params) => {
                const row = params.row;
                return (_jsx(Typography, { variant: 'body2', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', children: row.registered || '—' }));
            }
        },
        ...(isFamilyFest
            ? []
            : [
                {
                    field: 'actions',
                    headerName: '',
                    flex: 1.2,
                    minWidth: 160,
                    sortable: false,
                    filterable: false,
                    renderCell: (params) => {
                        const row = params.row;
                        return (_jsx(Box, { display: 'flex', alignItems: 'center', gap: 1, children: _jsx(Button, { variant: row.isCoordinator ? 'soft' : 'outlined', size: 'small', color: row.isCoordinator ? 'info' : 'primary', onClick: (event) => {
                                    event.stopPropagation();
                                    handleAssignCoordinatorClick(row);
                                }, children: row.isCoordinator
                                    ? t('events.eventDetails.transportTab.vehicleCoordinator')
                                    : t('events.eventDetails.transportTab.assignAsCoordinator') }) }));
                    }
                }
            ]),
        {
            field: 'actions2',
            headerName: '',
            flex: 1,
            minWidth: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const row = params.row;
                return (_jsx(IconButton, { size: 'small', color: 'inherit', onClick: (e) => {
                        e.stopPropagation();
                        setSelectedRowForMenu(row);
                        setRowMenuAnchorEl(e.currentTarget);
                    }, children: _jsx(MoreHorizIcon, {}) }));
            }
        }
    ];
    const handleBulkUnassignClick = () => {
        if (selectedRows.length > 0) {
            setConfirmBulkUnassignOpen(true);
        }
        setBulkMenuAnchorEl(null);
    };
    const handleUnassignClick = () => {
        if (selectedRowForMenu) {
            setAttendeeToUnassign(selectedRowForMenu);
            setConfirmUnassignOpen(true);
        }
        setRowMenuAnchorEl(null);
    };
    const handleConfirmUnassign = async (attendeeIds) => {
        if (attendeeIds.length === 0 || !vehicleId)
            return;
        try {
            let response;
            if (isFamilyFest) {
                response = await dispatch(unassignFamilyFestAttendeeFromVehicleAsync(attendeeIds)).unwrap();
            }
            else {
                response = await dispatch(unassignAttendeeFromVehicleAsync(attendeeIds)).unwrap();
            }
            if (response.isSuccess) {
                const isBulk = attendeeIds.length > 1;
                toast.success(response.message ||
                    (isBulk
                        ? t('events.eventDetails.transportTab.attendeesUnassignedSuccessfully', { count: attendeeIds.length })
                        : t('events.eventDetails.transportTab.attendeeUnassignedSuccessfully')));
                // Refresh the attendee list and location summary counts
                setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                if (isFamilyFest) {
                    dispatch(getFamilyFestAttendeesByVehicleIdAsync({
                        vehicleId: vehicleId,
                        pageNumber: 1,
                        pageSize: paginationModel.pageSize,
                        searchString: searchValue || undefined
                    }));
                    if (locationId)
                        dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
                }
                else {
                    dispatch(getAttendeesByVehicleIdAsync({
                        vehicleId: vehicleId,
                        pageNumber: 1,
                        pageSize: paginationModel.pageSize,
                        searchString: searchValue || undefined
                    }));
                    if (locationId)
                        dispatch(getPickupLocationByIdAsync(String(locationId)));
                }
                // Close dialogs and clear state
                setConfirmUnassignOpen(false);
                setAttendeeToUnassign(null);
                setConfirmBulkUnassignOpen(false);
                // Clear selections for bulk operation
                if (isBulk) {
                    setSelectedRows([]);
                }
            }
            else {
                const isBulk = attendeeIds.length > 1;
                toast.error(response.message ||
                    (isBulk
                        ? t('events.eventDetails.transportTab.attendeesUnassignFailed')
                        : t('events.eventDetails.transportTab.attendeeUnassignFailed')));
            }
        }
        catch (error) {
            const isBulk = attendeeIds.length > 1;
            const errorMessage = error?.message ||
                (isBulk
                    ? t('events.eventDetails.transportTab.attendeesUnassignFailed')
                    : t('events.eventDetails.transportTab.attendeeUnassignFailed'));
            toast.error(errorMessage);
        }
    };
    const handleConfirmBulkReassignPickupLocation = () => {
        if (!selectedPickupLocation)
            return;
        handleBulkReassignPickupLocation(selectedPickupLocation);
        setConfirmBulkReassignOpen(false);
        setSelectedPickupLocation(null);
    };
    const handleReassignVehicle = async (vehicle, attendeeIds) => {
        if (attendeeIds.length === 0)
            return;
        try {
            let response;
            if (isFamilyFest) {
                response = await dispatch(assignFamilyFestAttendeeToVehicleAsync({
                    attendeeIds: attendeeIds,
                    vehicleId: vehicle.id
                })).unwrap();
            }
            else {
                response = await dispatch(assignAttendeeToVehicleAsync({
                    attendeeIds: attendeeIds,
                    vehicleId: vehicle.id
                })).unwrap();
            }
            if (response.isSuccess) {
                const isBulk = attendeeIds.length > 1;
                toast.success(response.message ||
                    (isBulk
                        ? t('events.eventDetails.transportTab.attendeesAssignedSuccessfully', { count: attendeeIds.length })
                        : t('events.eventDetails.transportTab.attendeeAssignedSuccessfully')));
                // Refresh the attendee list
                if (vehicleId) {
                    if (isFamilyFest) {
                        dispatch(getFamilyFestAttendeesByVehicleIdAsync({
                            vehicleId: vehicleId,
                            pageNumber: paginationModel.page + 1,
                            pageSize: paginationModel.pageSize,
                            searchString: searchValue || undefined
                        }));
                    }
                    else {
                        dispatch(getAttendeesByVehicleIdAsync({
                            vehicleId: vehicleId,
                            pageNumber: paginationModel.page + 1,
                            pageSize: paginationModel.pageSize,
                            searchString: searchValue || undefined
                        }));
                    }
                }
                // Close dialogs and clear state
                setReassignVehicleDialogOpen(false);
                setAttendeeToReassign(null);
                setBulkReassignVehicleDialogOpen(false);
                // Clear selections for bulk operation
                if (isBulk) {
                    setSelectedRows([]);
                }
            }
            else {
                const isBulk = attendeeIds.length > 1;
                toast.error(response.message ||
                    (isBulk
                        ? t('events.eventDetails.transportTab.attendeesAssignFailed')
                        : t('events.eventDetails.transportTab.attendeeAssignFailed')));
            }
        }
        catch (error) {
            const isBulk = attendeeIds.length > 1;
            const errorMessage = error?.message ||
                (isBulk
                    ? t('events.eventDetails.transportTab.attendeesAssignFailed')
                    : t('events.eventDetails.transportTab.attendeeAssignFailed'));
            toast.error(errorMessage);
        }
    };
    const handleBulkReassignVehicleClick = () => {
        if (selectedRows.length > 0) {
            setBulkReassignVehicleDialogOpen(true);
        }
        setBulkMenuAnchorEl(null);
    };
    const handleBulkReassignPickupLocation = (location) => {
        if (selectedRows.length > 0) {
            const attendeeIds = selectedRows.map(id => String(id));
            if (isFamilyFest) {
                dispatch(reassignFamilyFestAttendeesToPickupLocationAsync({
                    attendeeIds: attendeeIds,
                    pickupLocationId: String(location.id)
                }))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        const isBulk = attendeeIds.length > 1;
                        toast.success(response.message ||
                            (isBulk
                                ? t('events.eventDetails.transportTab.attendeesReassignedToPickupLocationSuccessfully', { count: attendeeIds.length })
                                : t('events.eventDetails.transportTab.attendeeReassignedToPickupLocationSuccessfully')));
                        // Refresh the attendee list and location summary counts
                        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                        if (vehicleId) {
                            dispatch(getFamilyFestAttendeesByVehicleIdAsync({
                                vehicleId: vehicleId,
                                pageNumber: 1,
                                pageSize: paginationModel.pageSize,
                                searchString: searchValue || undefined
                            }));
                        }
                        if (locationId)
                            dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
                        // Clear selections for bulk operation
                        setSelectedRows([]);
                        setReassignPickupAnchorEl(null);
                    }
                    else {
                        const isBulk = attendeeIds.length > 1;
                        toast.error(response.message ||
                            (isBulk
                                ? t('events.eventDetails.transportTab.attendeesReassignToPickupLocationFailed')
                                : t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed')));
                    }
                })
                    .catch((error) => {
                    const isBulk = attendeeIds.length > 1;
                    const errorMessage = error?.message ||
                        (isBulk
                            ? t('events.eventDetails.transportTab.attendeesReassignToPickupLocationFailed')
                            : t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed'));
                    toast.error(errorMessage);
                });
            }
            else {
                dispatch(reassignAttendeesToPickupLocationAsync({
                    attendeeIds: attendeeIds,
                    pickupLocationId: String(location.id)
                }))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        const isBulk = attendeeIds.length > 1;
                        toast.success(response.message ||
                            (isBulk
                                ? t('events.eventDetails.transportTab.attendeesReassignedToPickupLocationSuccessfully', { count: attendeeIds.length })
                                : t('events.eventDetails.transportTab.attendeeReassignedToPickupLocationSuccessfully')));
                        // Refresh the attendee list and location summary counts
                        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                        if (vehicleId) {
                            dispatch(getAttendeesByVehicleIdAsync({
                                vehicleId: vehicleId,
                                pageNumber: 1,
                                pageSize: paginationModel.pageSize,
                                searchString: searchValue || undefined
                            }));
                        }
                        if (locationId)
                            dispatch(getPickupLocationByIdAsync(String(locationId)));
                        // Clear selections for bulk operation
                        setSelectedRows([]);
                        setReassignPickupAnchorEl(null);
                    }
                    else {
                        const isBulk = attendeeIds.length > 1;
                        toast.error(response.message ||
                            (isBulk
                                ? t('events.eventDetails.transportTab.attendeesReassignToPickupLocationFailed')
                                : t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed')));
                    }
                })
                    .catch((error) => {
                    const isBulk = attendeeIds.length > 1;
                    const errorMessage = error?.message ||
                        (isBulk
                            ? t('events.eventDetails.transportTab.attendeesReassignToPickupLocationFailed')
                            : t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed'));
                    toast.error(errorMessage);
                });
            }
        }
    };
    const handleReassignPickupLocationConfirm = (location) => {
        if (attendeeToReassignPickup) {
            if (isFamilyFest) {
                dispatch(reassignFamilyFestAttendeesToPickupLocationAsync({
                    attendeeIds: [attendeeToReassignPickup.id],
                    pickupLocationId: String(location.id)
                }))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        toast.success(response.message || t('events.eventDetails.transportTab.attendeeReassignedToPickupLocationSuccessfully'));
                        // Refresh the attendee list and location summary counts
                        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                        if (vehicleId) {
                            dispatch(getFamilyFestAttendeesByVehicleIdAsync({
                                vehicleId: vehicleId,
                                pageNumber: 1,
                                pageSize: paginationModel.pageSize,
                                searchString: searchValue || undefined
                            }));
                        }
                        if (locationId)
                            dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
                        setAttendeeToReassignPickup(null);
                        setReassignPickupDialogOpen(false);
                    }
                    else {
                        toast.error(response.message || t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed'));
                    }
                })
                    .catch((error) => {
                    const errorMessage = error?.message || t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed');
                    toast.error(errorMessage);
                });
            }
            else {
                dispatch(reassignAttendeesToPickupLocationAsync({
                    attendeeIds: [attendeeToReassignPickup.id],
                    pickupLocationId: String(location.id)
                }))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        toast.success(response.message || t('events.eventDetails.transportTab.attendeeReassignedToPickupLocationSuccessfully'));
                        // Refresh the attendee list and location summary counts
                        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                        if (vehicleId) {
                            dispatch(getAttendeesByVehicleIdAsync({
                                vehicleId: vehicleId,
                                pageNumber: 1,
                                pageSize: paginationModel.pageSize,
                                searchString: searchValue || undefined
                            }));
                        }
                        if (locationId)
                            dispatch(getPickupLocationByIdAsync(String(locationId)));
                        setAttendeeToReassignPickup(null);
                        setReassignPickupDialogOpen(false);
                    }
                    else {
                        toast.error(response.message || t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed'));
                    }
                })
                    .catch((error) => {
                    const errorMessage = error?.message || t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed');
                    toast.error(errorMessage);
                });
            }
        }
    };
    const rowMenuActions = [
        {
            text: t('events.eventDetails.transportTab.unassignAttendee'),
            group: true,
            to: handleUnassignClick
        },
        {
            text: t('events.eventDetails.transportTab.reassignPickupLocation'),
            group: true,
            to: () => {
                if (selectedRowForMenu) {
                    setAttendeeToReassignPickup(selectedRowForMenu);
                }
                setRowMenuAnchorEl(null);
                setReassignPickupDialogOpen(true);
            }
        },
        {
            text: t('events.eventDetails.transportTab.reassignVehicle'),
            group: true,
            to: () => {
                if (selectedRowForMenu) {
                    setAttendeeToReassign(selectedRowForMenu);
                    setReassignVehicleDialogOpen(true);
                }
                setRowMenuAnchorEl(null);
            }
        }
    ];
    const bulkMenuActions = [
        {
            text: t('events.eventDetails.transportTab.sendToUnassigned'),
            group: true,
            to: handleBulkUnassignClick
        },
        {
            text: t('events.eventDetails.transportTab.reassignVehicle'),
            group: true,
            to: handleBulkReassignVehicleClick
        }
    ];
    const dialogActions = (_jsx(Box, { display: "flex", width: "100%", children: _jsx(Button, { variant: 'outlined', color: 'inherit', onClick: () => { onClose(); setSelectedRows([]); }, size: 'large', fullWidth: true, children: t('actions.cancel') }) }));
    const content = (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(SearchFilter, { fullWidth: true, forceDesktopVariant: true, searchValue: searchValue, onSearchChange: setSearchValue, placeholder: t('events.eventDetails.transportTab.searchAttendees'), breakpoint: 'lg', onClear: () => setSearchValue(''), widthBox: '100%', bgVariant: 'grey8' }), _jsx(PrimaryGrid, { columns: columns, rows: filteredAttendees, isMultiple: !hideBulkActions, onSelectionChange: hideBulkActions ? undefined : (model) => setSelectedRows(model), rowSelectionModel: hideBulkActions ? [] : selectedRows, showHeaderControls: false, showNewHeaderControls: !hideBulkActions, paginationMode: 'server', paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, rowCount: vehicleAttendees?.totalCount || 0, sortingMode: isFamilyFest ? 'server' : 'client', sortModel: sortModel, onSortModelChange: (model) => {
                    setSortModel(model);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, pageSizeOptions: [10, 25, 50, 100], noRowsText: t('dataGrid.noRows', {
                    entityPlural: t('events.eventDetails.transportTab.attendees')?.toLowerCase(),
                    entitySingular: t('events.eventDetails.transportTab.attendee')?.toLowerCase()
                }) || t('dataGrid.noData'), customButton: selectedRows.length > 0 && (_jsx(_Fragment, { children: _jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px 8px 8px', bgcolor: theme.palette.background.neutral, borderRadius: '14px', children: [_jsx(Box, { display: 'flex', gap: 1, pl: 1, children: _jsx(Typography, { variant: 'body1', color: theme.palette.primary.main, children: t('common.countSelected', { count: selectedRows.length }) }) }), _jsxs(Box, { display: 'flex', gap: 0.5, children: [_jsx(Button, { variant: 'outlined', color: 'primary', size: 'medium', startIcon: _jsx(Location, { variant: 'Outline' }), onClick: (e) => setReassignPickupAnchorEl(e.currentTarget), children: t('events.eventDetails.transportTab.reassignPickupLocation') }), _jsx(IconButton, { size: 'medium', color: 'inherit', onClick: (e) => setBulkMenuAnchorEl(e.currentTarget), children: _jsx(MoreHorizIcon, {}) })] })] }) })), boxProps: {
                    sx: { display: 'flex', flexDirection: 'column', gap: '20px', height: 320, width: '100%' }
                } }), _jsx(ReassignPickupLocationMenu, { anchorEl: reassignPickupAnchorEl, open: Boolean(reassignPickupAnchorEl), onClose: () => {
                    setReassignPickupAnchorEl(null);
                    setPickupLocationSearchValue('');
                }, options: pickupLocationOptions, onSelect: (location) => {
                    setReassignPickupAnchorEl(null);
                    setSelectedPickupLocation(location);
                    setConfirmBulkReassignOpen(true);
                }, onSearchChange: setPickupLocationSearchValue }), _jsx(StyledMenu, { anchorEl: bulkMenuAnchorEl, open: Boolean(bulkMenuAnchorEl), onClose: () => setBulkMenuAnchorEl(null), actions: bulkMenuActions, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } }), _jsx(StyledMenu, { anchorEl: rowMenuAnchorEl, open: Boolean(rowMenuAnchorEl), onClose: () => {
                    setRowMenuAnchorEl(null);
                    setSelectedRowForMenu(null);
                }, actions: rowMenuActions, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } }), _jsx(ConfirmationDialog, { open: confirmCoordinatorOpen, onClose: () => {
                    setConfirmCoordinatorOpen(false);
                    setPendingCoordinatorId(null);
                }, title: t('events.eventDetails.transportTab.confirmCoordinatorChangeTitle'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                setConfirmCoordinatorOpen(false);
                                setPendingCoordinatorId(null);
                            }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', size: 'large', color: 'inherit', onClick: async () => {
                                if (pendingCoordinatorId != null) {
                                    await applyCoordinator(pendingCoordinatorId);
                                }
                                setConfirmCoordinatorOpen(false);
                                setPendingCoordinatorId(null);
                            }, children: t('actions.confirm') })] }), children: _jsx(Box, { p: 2, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.transportTab.confirmCoordinatorChangeBody', {
                            previous: coordinatorName ?? currentCoordinator?.name ?? '',
                            next: pendingCoordinator?.name ?? ''
                        }) }) }) }), _jsx(ReassignPickupLocationDialog, { open: reassignPickupDialogOpen, pickupLocationId: locationId, isFamilyFest: isFamilyFest, onClose: () => {
                    setAttendeeToReassignPickup(null);
                    setReassignPickupDialogOpen(false);
                }, onConfirm: handleReassignPickupLocationConfirm }), _jsx(ReassignVehicleDialog, { open: reassignVehicleDialogOpen, onClose: () => {
                    setReassignVehicleDialogOpen(false);
                    setAttendeeToReassign(null);
                }, locationId: locationId, isFamilyFest: isFamilyFest, onConfirm: (vehicle) => {
                    if (attendeeToReassign) {
                        handleReassignVehicle(vehicle, [attendeeToReassign.id]);
                    }
                } }), _jsx(ReassignVehicleDialog, { open: bulkReassignVehicleDialogOpen, onClose: () => {
                    setBulkReassignVehicleDialogOpen(false);
                }, locationId: locationId, isFamilyFest: isFamilyFest, onConfirm: (vehicle) => {
                    const attendeeIds = selectedRows.map(id => String(id));
                    handleReassignVehicle(vehicle, attendeeIds);
                } }), _jsx(ConfirmationDialog, { open: confirmUnassignOpen, onClose: () => {
                    setConfirmUnassignOpen(false);
                    setAttendeeToUnassign(null);
                }, title: t('events.eventDetails.transportTab.unassignAttendee'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                setConfirmUnassignOpen(false);
                                setAttendeeToUnassign(null);
                            }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', size: 'large', color: 'inherit', onClick: () => {
                                if (attendeeToUnassign) {
                                    handleConfirmUnassign([attendeeToUnassign.id]);
                                }
                            }, children: t('actions.confirm') })] }), children: _jsx(Box, { p: 2, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.transportTab.confirmUnassignAttendee', {
                            name: attendeeToUnassign?.name,
                            vehicleNumber: vehicleNumber || ''
                        }) }) }) }), _jsx(ConfirmationDialog, { open: confirmBulkUnassignOpen, onClose: () => {
                    setConfirmBulkUnassignOpen(false);
                }, title: t('events.eventDetails.transportTab.bulkUnassignAttendees'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                setConfirmBulkUnassignOpen(false);
                            }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', size: 'large', color: 'inherit', onClick: () => {
                                const attendeeIds = selectedRows.map(id => String(id));
                                handleConfirmUnassign(attendeeIds);
                            }, children: t('actions.confirm') })] }), children: _jsx(Box, { p: 2, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.transportTab.confirmBulkUnassignAttendees', {
                            count: selectedRows.length,
                            vehicleNumber: vehicleNumber || ''
                        }) }) }) }), _jsx(ConfirmationDialog, { open: confirmBulkReassignOpen, onClose: () => {
                    setConfirmBulkReassignOpen(false);
                }, title: t('events.eventDetails.transportTab.bulkReassignPickupLocationAttendees'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                setConfirmBulkReassignOpen(false);
                            }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', size: 'large', color: 'inherit', onClick: handleConfirmBulkReassignPickupLocation, children: t('actions.confirm') })] }), children: _jsx(Box, { p: 2, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.transportTab.confirmBulkReassignPickupLocationAttendees_plural', {
                            count: selectedRows.length,
                            vehicleNumber: vehicleNumber || ''
                        }) }) }) })] }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { open: open, onClose: onClose, title: t('events.eventDetails.transportTab.viewAttendees'), anchor: 'bottom', showBottomBar: true, dialogActions: dialogActions, children: _jsx(Box, { sx: { p: 2 }, children: content }) }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: onClose, titleWithIcon: t('events.eventDetails.transportTab.viewAttendees'), titleIcon: '/assets/images/add-person.png', maxWidth: 'md', backgroundVariant: 'white', closeIcon: true, actions: dialogActions, children: _jsx(Box, { p: 2, sx: {}, children: content }) }));
};
export default ViewAttendeesDialog;
